import { Container, ContainerSize, Gap, Heading, LabelOverflow, ListGroup, Progress, ProgressColor, ProgressWidth, Stack, StackDirection, Text } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";
type ProgressProps = {}

const ProgressPage = ({ }: ProgressProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Progress bar
            </Heading>

            <Text className="mb-4">
                The <Text el="strong">Progress</Text> component is used to visually indicate the completion or status of
                a task. It can display
                percentages, show animated stripes, or use different colors to indicate the type or importance of
                progress.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Usage
            </Heading>
            <Text className="mb-4">
                You can use this component for task completion bars, loading indicators, or visual feedback in forms and
                dashboards.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column} gap={Gap.Gap2}>
                        <Progress progressValue={25} color={ProgressColor.Primary} />
                        <Progress progressValue={50} color={ProgressColor.Primary} />
                        <Progress progressValue={75} color={ProgressColor.Primary} />
                        <Progress progressValue={100} color={ProgressColor.Primary} />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ProgressValueCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Text className="mb-4">
                The <Text el="strong">progressValueClass prop</Text> uses predefined width classes (ProgressWidth.W25,
                ProgressWidth.W50,
                etc.). These classes correspond to <Text el="strong">fixed progress values — 0%, 25%, 50%, 75%, and
                    100%</Text>. This makes it
                quick to configure standard progress bar levels without manually entering numeric values.

                <ListGroup className="my-2">
                    <ListGroup.Item className="border-0 p-0"><Text el="strong">ProgressValue</Text> → for custom
                        percentage values (e.g., progressValue={40}),</ListGroup.Item>
                    <ListGroup.Item className="border-0 p-0"><Text el="strong">progressValueClass</Text> → for predefined
                        fixed widths (0, 25, 50, 75, 100).</ListGroup.Item>
                    <ListGroup.Item className="border-0 p-0"><Text el="strong">Both props serve the same purpose but
                        cannot be used together — only one should be applied at a time.</Text></ListGroup.Item>
                </ListGroup>
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column} gap={Gap.Gap2}>
                        <Progress progressValueClass={ProgressWidth.W25}
                            color={ProgressColor.Primary} />
                        <Progress progressValueClass={ProgressWidth.W50}
                            color={ProgressColor.Primary} />
                        <Progress progressValueClass={ProgressWidth.W75}
                            color={ProgressColor.Primary} />
                        <Progress progressValueClass={ProgressWidth.W100}
                            color={ProgressColor.Primary} />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ProgressValueClassCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Label
            </Heading>
            <Text className="mb-4">
                You can add a <Text el="strong">label</Text> inside the progress bar to visually show the completion
                percentage or any text.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Progress progressValue={25} color={ProgressColor.Primary} label="25%" />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ProgressLabelCode}
                </CodeHighlighter>
            </CodeSnippet>
            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column} gap={Gap.Gap2}>
                        <Progress progressValue={25} color={ProgressColor.Primary}
                            label="Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the" />
                        <Progress progressValue={25} color={ProgressColor.Primary}
                            labelOverflow={LabelOverflow.Visible}
                            label="Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the" />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ProgressLabelOverflowCode}
                </CodeHighlighter>
            </CodeSnippet>
            <Text className="mb-4">
                By default, if the <Text el="strong">label</Text> text is longer than the <Text el="strong">progress
                    bar’s filled area</Text> (i.e., exceeds the current
                progress value), the overflowing text is hidden.

                You can change this behavior by setting the <Text el="strong">labelOverflow prop</Text> to <Text
                    el="strong">LabelOverflow.Visible</Text>, which
                allows the label to remain fully visible, even beyond the filled portion of the bar.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Height
            </Heading>
            <Text className="mb-4">
                You can easily make progress bars of different thicknesses by setting the <Text el="strong">height
                    prop</Text>. This prop controls the vertical size of the <Text el="strong">progress bar</Text>, allowing
                you to create thinner or
                thicker bars depending on your
                design needs.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column} gap={Gap.Gap2}>
                        <Progress progressValue={25} color={ProgressColor.Primary} height={2} />
                        <Progress progressValue={50} color={ProgressColor.Primary} height={20} />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ProgressHeightCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Custom classes
            </Heading>
            <Text className="mb-4">
                Use <Text el="strong">className</Text> for the outer <Text el="strong">.progress</Text> wrapper (for example
                width or spacing utilities). Use <Text el="strong">barClassName</Text> for the inner <Text
                    el="strong">.progress-bar</Text> element. If a token conflicts with a prop-driven class (such as <Text
                        el="strong">color</Text> or <Text el="strong">isStriped</Text>), the prop wins and the duplicate is
                omitted from the merged class list.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column} gap={Gap.Gap2}>
                        <Progress
                            className="w-75"
                            barClassName="fw-semibold"
                            progressValue={60}
                            color={ProgressColor.Warning}
                            label="60%"
                        />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ProgressCustomClassesCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Background
            </Heading>
            <Text className="mb-4">
                Set the <Text el="strong">color</Text> prop to a <Text el="strong">ProgressColor</Text> value to change the
                fill color of the bar (Bootstrap background utilities such as <Text el="strong">bg-primary</Text>, <Text
                    el="strong">bg-success</Text>, and so on).
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column} gap={Gap.Gap2}>
                        <Progress progressValue={25} color={ProgressColor.Primary} />
                        <Progress progressValue={50} color={ProgressColor.Warning} />
                        <Progress progressValue={75} color={ProgressColor.Success} />
                        <Progress progressValue={100} color={ProgressColor.Danger} />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ProgressColorClassCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Striped
            </Heading>
            <Text className="mb-4">
                You can add a <Text el="strong">striped pattern</Text> to any <Text el="strong">progress bar</Text> by
                setting the <Text el="strong">isStriped prop</Text> to <Text el="strong">true</Text>.
                This gives the bar a visual texture that indicates ongoing progress or activity.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column} gap={Gap.Gap2}>
                        <Progress progressValue={25} color={ProgressColor.Primary}
                            isStriped={true} />
                        <Progress progressValue={50} color={ProgressColor.Warning}
                            isStriped={true} />
                        <Progress progressValue={75} color={ProgressColor.Success}
                            isStriped={true} />
                        <Progress progressValue={100} color={ProgressColor.Danger}
                            isStriped={true} />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ProgressStripedCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Animated stripes
            </Heading>
            <Text className="mb-4">
                To create a moving, animated stripe effect on the progress bar, set <Text
                    el="strong">isAnimating</Text> to <Text el="strong">true</Text> — but make sure <Text
                        el="strong">isStriped</Text> is set to <Text el="strong">true</Text> is also applied.
                The animation creates a smooth motion across the stripes, giving the impression of an active or loading
                process.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column} gap={Gap.Gap2}>
                        <Progress progressValue={25} color={ProgressColor.Primary}
                            isStriped={true} isAnimating={true} />
                        <Progress progressValue={50} color={ProgressColor.Warning}
                            isStriped={true} isAnimating={true} />
                        <Progress progressValue={75} color={ProgressColor.Success}
                            isStriped={true} isAnimating={true} />
                        <Progress progressValue={100} color={ProgressColor.Danger}
                            isStriped={true} isAnimating={true} />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ProgressAnimatedStripedCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    )
}

export default ProgressPage;

const ProgressValueCode = `<Stack direction={StackDirection.Column} gap={Gap.Gap2}>
    <Progress progressValue={25} color={ProgressColor.Primary}/>
    <Progress progressValue={50} color={ProgressColor.Primary}/>
    <Progress progressValue={75} color={ProgressColor.Primary}/>
    <Progress progressValue={100} color={ProgressColor.Primary}/>
</Stack>`;

const ProgressValueClassCode = `<Stack direction={StackDirection.Column} gap={Gap.Gap2}>
    <Progress progressValueClass={ProgressWidth.W25} color={ProgressColor.Primary}/>
    <Progress progressValueClass={ProgressWidth.W50} color={ProgressColor.Primary}/>
    <Progress progressValueClass={ProgressWidth.W75} color={ProgressColor.Primary}/>
    <Progress progressValueClass={ProgressWidth.W100} color={ProgressColor.Primary}/>
</Stack>`;

const ProgressHeightCode = `<Stack direction={StackDirection.Column} gap={Gap.Gap2}>
     <Progress progressValue={25} color={ProgressColor.Primary} height={2}/>
     <Progress progressValue={50} color={ProgressColor.Primary} height={20}/>
</Stack>`;

const ProgressCustomClassesCode = `<Stack direction={StackDirection.Column} gap={Gap.Gap2}>
    <Progress
        className="w-75"
        barClassName="fw-semibold"
        progressValue={60}
        color={ProgressColor.Warning}
        label="60%"
    />
</Stack>`;

const ProgressLabelCode = `<Progress progressValue={25} color={ProgressColor.Primary} label="25%"/>`

const ProgressLabelOverflowCode = `<Stack direction={StackDirection.Column} gap={Gap.Gap2}>
   <Progress progressValue={25} color={ProgressColor.Primary} label="Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the"/>
   <Progress progressValue={25} color={ProgressColor.Primary} labelOverflow={LabelOverflow.Visible} label="Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the"/>
</Stack>`;

const ProgressColorClassCode = `<Stack direction={StackDirection.Column} gap={Gap.Gap2}>
     <Progress progressValue={25} color={ProgressColor.Primary}/>
     <Progress progressValue={50} color={ProgressColor.Warning}/>
     <Progress progressValue={75} color={ProgressColor.Success}/>
     <Progress progressValue={100} color={ProgressColor.Danger}/>
</Stack>`;

const ProgressStripedCode = `<Stack direction={StackDirection.Column} gap={Gap.Gap2}>
    <Progress progressValue={25} color={ProgressColor.Primary} isStriped={true}/>
    <Progress progressValue={50} color={ProgressColor.Warning} isStriped={true}/>
    <Progress progressValue={75} color={ProgressColor.Success} isStriped={true}/>
    <Progress progressValue={100} color={ProgressColor.Danger} isStriped={true}/>
</Stack>`;

const ProgressAnimatedStripedCode = `<Stack direction={StackDirection.Column} gap={Gap.Gap2}>
    <Progress progressValue={25} color={ProgressColor.Primary} isStriped={true} isAnimating={true}/>
    <Progress progressValue={50} color={ProgressColor.Warning} isStriped={true} isAnimating={true}/>
    <Progress progressValue={75} color={ProgressColor.Success} isStriped={true} isAnimating={true}/>
    <Progress progressValue={100} color={ProgressColor.Danger} isStriped={true} isAnimating={true}/>
</Stack>`;
